import * as React from 'react';
import cn from 'classnames';
import {
    Menu as BaseMenu,
    MenuButton,
    MenuItem as BaseMenuItem,
    useMenuItem,
    MenuSeparator,
    MenuItemCheckbox,
    MenuItemRadio,
    useMenuState,
    MenuDisclosureProps,
    MenuItemProps as BaseMenuItemProps,
    MenuSeparatorProps,
    MenuItemOptions,
    MenuItemCheckboxOptions,
    MenuItemRadioOptions,
} from 'reakit/Menu';
import './Menu.css';
import { ModalProps } from '../Modal/Modal';
import { useBoundingClientRectListener } from '../../utils/hooks/useBoundingClientRectListener';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef';

export type MenuProps = React.HTMLAttributes<HTMLElement> & {
    /**
     * Content of Menu should be one of the Menu's components, e.g. `Menu.Item`.
     * Read about `FowardedMenuWithStatics` for more details
     */
    children: React.ReactElement | React.ReactElement[];
    /** Defines the component that controls visibility of the menu */
    disclosure?: React.ReactElement<React.HTMLAttributes<any>>;
    /**
     * Handler called when the values provided for menu change.
     * To be used toghether with `value` prop and `Menu.ItemCheckbox`, `Menu.ItemRadio` components
     */
    onChange?: (values: object) => void;
    /**
     * Values provided for radios and checkboxes within the menu.
     * To be used toghether with `onChange` handler and `Menu.ItemCheckbox`, `Menu.ItemRadio` components
     */
    value?: object;
};

export type MenuLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;

export type MenuItemProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    /**
     * [Modal](component:modal) component associated with the menu item.
     * The modal opens when menu item is clicked.
     */
    modal?: ({ disclosure }: Partial<ModalProps>) => React.ReactElement<ModalProps>;
};

export type MenuItemCheckboxProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
    Pick<MenuItemCheckboxOptions, 'name' | 'value'>;

export type MenuItemRadioProps = React.ButtonHTMLAttributes<HTMLButtonElement> & Pick<MenuItemRadioOptions, 'name' | 'value'>;

export type FowardedMenuWithStatics = React.ForwardRefExoticComponent<MenuProps & React.RefAttributes<HTMLButtonElement>> & {
    /** A button that follows wai-aria recommendations for a menu item */
    Item: (props: MenuItemProps) => JSX.Element | null;
    /**
     * A toggle button that follows wai-aria recommendations for a menu item checkbox.
     * It displays a tick icon before the actual text of the item when having a checked state
     */
    ItemCheckbox: (props: MenuItemCheckboxProps) => JSX.Element;
    /**
     * A button that follows wai-aria recommendations for a menu item radio.
     * It displays a small dot before the actual text of the item when having a checked state
     */
    ItemRadio: (props: MenuItemRadioProps) => JSX.Element;
    /** A link that follows wai-aria recommendations for a menu item */
    Link: (props: MenuLinkProps) => JSX.Element | null;
    /**
     * A divider that separates groups of menu items by proviving a visible boundary between them.
     * It follows wai-aria recommendations for separator role
     */
    Separator: (props: MenuSeparatorProps) => JSX.Element;
};

export const Menu = React.forwardRef(function Menu(props: MenuProps, ref: React.Ref<HTMLButtonElement>) {
    const { children, disclosure, onChange, value, ...otherProps } = props;
    const className = cn('yt-menu', props.className);
    const internalRef = useProxiedRef<HTMLButtonElement>(ref);
    const buttonDimensions = useBoundingClientRectListener(internalRef);
    const menu = useMenuState({
        unstable_values: value, // eslint-disable-line @typescript-eslint/camelcase
    });

    const { modal, ...menuProps } = menu;
    const childrenWithProps = React.Children.map(children, (child: React.ReactElement) =>
        React.cloneElement(child, {
            ...menuProps,
            hide: () => {
                menu.hide();

                if (child.props.hide) {
                    child.props.hide();
                }
            },
        })
    );

    React.useEffect(() => {
        if (onChange) {
            onChange(menu.unstable_values);
        }
    }, [menu.unstable_values]);

    return (
        <>
            {disclosure && (
                <MenuButton {...menu} ref={internalRef}>
                    {({ type, ...disclosureProps }: MenuDisclosureProps) =>
                        React.cloneElement(React.Children.only(disclosure), disclosureProps)
                    }
                </MenuButton>
            )}
            <BaseMenu {...otherProps} {...menu} className={className} style={{ minWidth: buttonDimensions?.width }}>
                {menu.visible && childrenWithProps}
            </BaseMenu>
        </>
    );
}) as FowardedMenuWithStatics;

Menu.Link = (props: MenuLinkProps & { hide: () => void }) => {
    const { children, href, hide, onClick, target, ...otherProps } = props;
    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        event.persist();

        if (hide) {
            hide();
        }

        if (onClick) {
            onClick(event);
        }
    };
    const menuItemProps = useMenuItem(otherProps as MenuItemOptions);

    return <a {...menuItemProps} children={children} href={href} onClick={handleClick} target={target} />;
};
Menu.Item = (props: MenuItemProps & { hide: () => void }) => {
    const { hide, modal, onClick, ...otherProps } = props;
    const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        event.persist();

        if (hide) {
            hide();
        }

        if (onClick) {
            onClick(event);
        }
    };
    const disclosure = <BaseMenuItem {...(otherProps as BaseMenuItemProps)} onClick={handleClick} />;

    if (modal) {
        return modal({
            disclosure,
            onHide: hide,
        });
    }

    return disclosure;
};
Menu.Separator = MenuSeparator;
Menu.ItemCheckbox = MenuItemCheckbox;
Menu.ItemRadio = MenuItemRadio;
